import type { FoodParseRequest, FoodParseResponse, ParsedFood } from '@circa/shared/protocol';
import { api } from '@/core/api';
import { batch, create, update } from '@/core/repo';
import { store } from '@/core/store';
import { appZone, dayInZone, zoneOffsetMs } from '@/core/time';
import type { FoodEntry, FoodItem, Meal, Nutrition } from '@/core/types';

// Logging food fast: everything you've eaten before is in the library (FoodItem) and logs with
// one tap, locally, with no network. Only new foods go to the AI, and what it returns joins
// the library so it's instant next time.

export const MEALS: { id: Meal; label: string }[] = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch', label: 'Lunch' },
  { id: 'dinner', label: 'Dinner' },
  { id: 'snack', label: 'Snacks' },
];

/** The meal a moment most likely belongs to, on the wall clock. */
export function mealAt(ms: number, tz = appZone()): Meal {
  const local = new Date(ms + zoneOffsetMs(tz, ms));
  const h = local.getUTCHours() + local.getUTCMinutes() / 60;
  if (h >= 4.5 && h < 10.5) return 'breakfast';
  if (h >= 11 && h < 15) return 'lunch';
  if (h >= 17.5 && h < 22) return 'dinner';
  return 'snack';
}

const NUTRIENT_KEYS = ['kcal', 'protein', 'carbs', 'fat', 'fiber', 'sugar', 'sodiumMg'] as const;

export function scale(n: Nutrition, k: number): Nutrition {
  const out: Nutrition = {
    kcal: n.kcal * k,
    protein: n.protein * k,
    carbs: n.carbs * k,
    fat: n.fat * k,
  };
  if (n.fiber !== undefined) out.fiber = n.fiber * k;
  if (n.sugar !== undefined) out.sugar = n.sugar * k;
  if (n.sodiumMg !== undefined) out.sodiumMg = n.sodiumMg * k;
  if (n.nutrients)
    out.nutrients = Object.fromEntries(Object.entries(n.nutrients).map(([key, v]) => [key, v * k]));
  return out;
}

export function nutritionOf(n: Nutrition): Nutrition {
  const out = {} as Nutrition;
  for (const k of NUTRIENT_KEYS)
    if (n[k] !== undefined) (out as unknown as Record<string, number>)[k] = n[k]!;
  if (n.nutrients) out.nutrients = { ...n.nutrients };
  return out;
}

export interface Totals {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodiumMg: number;
  nutrients: Record<string, number>;
  count: number;
}

export function totals(entries: FoodEntry[]): Totals {
  const t: Totals = {
    kcal: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
    sugar: 0,
    sodiumMg: 0,
    nutrients: {},
    count: 0,
  };
  for (const e of entries) {
    if (e.deleted) continue;
    t.count++;
    t.kcal += e.kcal || 0;
    t.protein += e.protein || 0;
    t.carbs += e.carbs || 0;
    t.fat += e.fat || 0;
    t.fiber += e.fiber || 0;
    t.sugar += e.sugar || 0;
    t.sodiumMg += e.sodiumMg || 0;
    for (const [k, v] of Object.entries(e.nutrients ?? {}))
      t.nutrients[k] = (t.nutrients[k] ?? 0) + (v || 0);
  }
  return t;
}

export function entriesOn(day: string): FoodEntry[] {
  return store.list('food').filter((e) => e.day === day && !e.deleted);
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

/** The library item with this name (and serving, when given). */
export function findItem(name: string, serving?: string): FoodItem | undefined {
  const n = norm(name);
  const sv = serving ? norm(serving) : undefined;
  return store
    .list('foodItem')
    .find(
      (i) => !i.deleted && norm(i.name) === n && (sv === undefined || norm(i.serving ?? '') === sv),
    );
}

/** Foods to offer first: favorites, then what you have most and most recently (by meal). */
export function suggestions(meal: Meal, limit = 12): FoodItem[] {
  const now = Date.now();
  const score = (i: FoodItem) =>
    (i.favorite ? 1000 : 0) +
    i.uses * (i.meal === meal ? 2 : 1) +
    (i.lastUsedAt ? Math.max(0, 30 - (now - i.lastUsedAt) / 86_400_000) : 0);
  return store
    .list('foodItem')
    .filter((i) => !i.deleted)
    .sort((a, b) => score(b) - score(a))
    .slice(0, limit);
}

/** Instant library search: every word must appear in the name. */
export function searchItems(q: string, limit = 30): FoodItem[] {
  const words = norm(q).split(' ').filter(Boolean);
  if (!words.length) return [];
  return store
    .list('foodItem')
    .filter((i) => !i.deleted && words.every((w) => norm(i.name).includes(w)))
    .sort((a, b) => b.uses - a.uses || (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))
    .slice(0, limit);
}

interface LogOpts {
  at?: number;
  meal?: Meal;
  quantity?: number;
  tz?: string;
}

function entryFrom(
  base: { name: string; serving?: string; grams?: number } & Nutrition,
  source: FoodEntry['source'],
  o: LogOpts,
  itemId?: string,
): Omit<FoodEntry, 'id' | 'kind' | 'createdAt' | 'updatedAt'> {
  const at = o.at ?? Date.now();
  const tz = o.tz ?? appZone();
  const q = o.quantity ?? 1;
  return {
    ...scale(nutritionOf(base), q),
    name: base.name,
    serving: base.serving,
    grams: base.grams !== undefined ? base.grams * q : undefined,
    quantity: q,
    at,
    tz,
    day: dayInZone(at, tz),
    meal: o.meal ?? mealAt(at, tz),
    source,
    itemId,
  };
}

/** One tap: log a library food (one serving unless told otherwise). */
export function logItem(item: FoodItem, o: LogOpts = {}): FoodEntry {
  let e!: FoodEntry;
  batch(() => {
    e = create('food', entryFrom(item, 'library', o, item.id));
    update('foodItem', item.id, {
      uses: item.uses + 1,
      lastUsedAt: e.at,
      meal: item.meal ?? e.meal,
    });
  });
  return e;
}

/** Save what the AI recognised: as entries, and in the library (per one serving). */
export function logParsed(
  items: ParsedFood[],
  source: 'ai' | 'photo',
  o: LogOpts = {},
): FoodEntry[] {
  const out: FoodEntry[] = [];
  batch(() => {
    for (const p of items) {
      const q = p.quantity > 0 ? p.quantity : 1;
      const perServing: Nutrition & { name: string; serving?: string; grams?: number } = {
        name: p.name,
        serving: p.serving,
        grams: p.grams ? p.grams / q : undefined,
        ...scale(
          {
            kcal: p.kcal,
            protein: p.protein,
            carbs: p.carbs,
            fat: p.fat,
            fiber: p.fiber,
            sugar: p.sugar,
            sodiumMg: p.sodiumMg,
            nutrients: p.nutrients,
          },
          1 / q,
        ),
      };
      const existing = findItem(p.name, p.serving);
      const item = existing ?? create('foodItem', { ...perServing, uses: 0, meal: o.meal });
      const e = create('food', entryFrom(perServing, source, { ...o, quantity: q }, item.id));
      update('foodItem', item.id, { uses: (existing?.uses ?? 0) + 1, lastUsedAt: e.at });
      out.push(e);
    }
  });
  return out;
}

/** Ask the server's AI what a description or photo contains. */
export function parseFood(req: FoodParseRequest): Promise<FoodParseResponse> {
  return api<FoodParseResponse>('/api/food/parse', { method: 'POST', body: req });
}

/** Shrink a photo to ~1024 px JPEG (fast to send, plenty for recognising food). */
export async function photoToDataUrl(file: Blob, max = 1024): Promise<string> {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * k);
  canvas.height = Math.round(bmp.height * k);
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close();
  return canvas.toDataURL('image/jpeg', 0.82);
}

// ------------------------------------------------------------------ nutrients

/** Daily values (adult) for the micronutrients the app tracks, keyed like the data. */
export const DAILY_VALUES: Record<string, { label: string; dv: number; unit: string }> = {
  saturated_fat_g: { label: 'Saturated fat', dv: 20, unit: 'g' },
  cholesterol_mg: { label: 'Cholesterol', dv: 300, unit: 'mg' },
  potassium_mg: { label: 'Potassium', dv: 4700, unit: 'mg' },
  calcium_mg: { label: 'Calcium', dv: 1300, unit: 'mg' },
  iron_mg: { label: 'Iron', dv: 18, unit: 'mg' },
  magnesium_mg: { label: 'Magnesium', dv: 420, unit: 'mg' },
  zinc_mg: { label: 'Zinc', dv: 11, unit: 'mg' },
  vitamin_a_mcg: { label: 'Vitamin A', dv: 900, unit: 'mcg' },
  vitamin_c_mg: { label: 'Vitamin C', dv: 90, unit: 'mg' },
  vitamin_d_mcg: { label: 'Vitamin D', dv: 20, unit: 'mcg' },
  vitamin_e_mg: { label: 'Vitamin E', dv: 15, unit: 'mg' },
  vitamin_k_mcg: { label: 'Vitamin K', dv: 120, unit: 'mcg' },
  vitamin_b6_mg: { label: 'Vitamin B6', dv: 1.7, unit: 'mg' },
  vitamin_b12_mcg: { label: 'Vitamin B12', dv: 2.4, unit: 'mcg' },
  folate_mcg: { label: 'Folate', dv: 400, unit: 'mcg' },
  thiamin_mg: { label: 'Thiamin', dv: 1.2, unit: 'mg' },
  riboflavin_mg: { label: 'Riboflavin', dv: 1.3, unit: 'mg' },
  niacin_mg: { label: 'Niacin', dv: 16, unit: 'mg' },
  choline_mg: { label: 'Choline', dv: 550, unit: 'mg' },
  selenium_mcg: { label: 'Selenium', dv: 55, unit: 'mcg' },
  phosphorus_mg: { label: 'Phosphorus', dv: 1250, unit: 'mg' },
  omega3_g: { label: 'Omega-3', dv: 1.6, unit: 'g' },
  added_sugar_g: { label: 'Added sugar', dv: 50, unit: 'g' },
};

/** Nutrients you should keep under, not reach. */
export const LIMIT_NUTRIENTS = new Set(['saturated_fat_g', 'cholesterol_mg', 'added_sugar_g']);
